import React, { useEffect, useState } from "react";
import { Row, Col, Modal, Form } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import ".././patients.css";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import GetData from "../../../services/getData";
import logPatientChange from "../../../services/patientLogs";
import { buildPatientLogText } from "../../../services/patientLogMessages";
import BlockTitle from "../../../components/layout/blockTitle/BlockTitle";
import TiptapEditor from "../../../components/layout/tiptapEditor/TiptapEditor";
import TiptapViewer from "../../../components/layout/tiptapViewer/TiptapViewer";
import SelectModal from "../../../components/layout/selectModal/SelectModal";
import "./PatientPlan.css";
import "./patientListAppointments.css";
import "./patientRecommendations.css";
import LogoText from "../../../media/logo_text.webp";

const emptyRecommendation = () => ({
  title: { value: "" },
  description: { value: "" },
});

const normalizeComparableValue = (value) =>
  value == null ? "" : String(value).trim();

const normalizeDisplayValue = (value) => {
  const prepared = String(value ?? "").trim();
  return prepared || "—";
};

const getTemplateDisplayName = (template, t) =>
  template?.title?.value ||
  template?.name?.value ||
  t("recommendations.templateFallback", { id: template?.id?.value ?? "—" });

function formatDateShortHeader(dateString) {
  if (
    !dateString ||
    dateString === "0000-00-00 00:00" ||
    dateString === "0000-00-00 00:00:00"
  ) {
    return "—";
  }
  const [datePart] = dateString.split(" ");
  const [year, month, day] = datePart.split("-");
  if (!year || !month || !day) return "—";
  return `${day}.${month}.${year}`;
}

function getFirstPlanOrActualDateFromAdmissions(admissions) {
  if (!admissions) return null;

  const isValidDate = (dateString) =>
    dateString &&
    dateString !== "0000-00-00 00:00" &&
    dateString !== "0000-00-00 00:00:00" &&
    !isNaN(new Date(dateString).getTime());

  let firstPlanDate = null;
  let firstActualDate = null;

  Object.values(admissions).forEach((adm) => {
    const plan = adm?.date_plan?.value;
    const actual = adm?.date?.value;

    if (isValidDate(plan)) {
      if (!firstPlanDate || new Date(plan) < new Date(firstPlanDate)) {
        firstPlanDate = plan;
      }
    }

    if (isValidDate(actual)) {
      if (!firstActualDate || new Date(actual) < new Date(firstActualDate)) {
        firstActualDate = actual;
      }
    }
  });

  if (!firstPlanDate && !firstActualDate) return null;
  if (!firstPlanDate) return firstActualDate;
  if (!firstActualDate) return firstPlanDate;

  return new Date(firstPlanDate) < new Date(firstActualDate)
    ? firstPlanDate
    : firstActualDate;
}

function PatientRecommendations() {
  const { t } = useTranslation("patient");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { patientId, planId, recommendationId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const currentPlanId = Number(planId) || 0;
  const currentRecommendationId = Number(recommendationId) || 0;
  const isListMode = currentRecommendationId <= 0;

  const [recommendationsList, setRecommendationsList] = useState([]);
  const [recommendations, setRecommendations] = useState(emptyRecommendation());
  const [savedRecommendations, setSavedRecommendations] = useState(emptyRecommendation());
  const [isEditing, setIsEditing] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");

  const [showTemplatesModal, setShowTemplatesModal] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templateType, setTemplateType] = useState("common");

  const [appointmentsPrint, setAppointmentsPrint] = useState(null);

  useEffect(() => {
    loadRecommendations();
  }, [patientId, planId, recommendationId]);

  useEffect(() => {
    if (!patientId) return;
    let cancelled = false;
    (async () => {
      try {
        const apts = await GetData({
          module: "patient",
          block: "list_appointments",
          id_patient: patientId,
        });
        if (!cancelled) {
          setAppointmentsPrint(apts);
        }
      } catch (e) {
        console.error(e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [patientId]);

  const showAlert = (message) => {
    setModalMessage(message);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const goToList = () => {
    if (currentPlanId > 0) {
      navigate(`/patient/${patientId}/plan/${currentPlanId}/recommendations`);
      return;
    }
    navigate(`/patient/${patientId}/recommendations`);
  };

  const goToRecommendation = (id, { edit = false } = {}) => {
    const nextId = Number(id);
    if (!Number.isFinite(nextId) || nextId <= 0) return;
    if (currentPlanId > 0) {
      navigate(
        `/patient/${patientId}/plan/${currentPlanId}/recommendations/${nextId}`,
        { state: edit ? { edit: true } : null },
      );
      return;
    }
    navigate(`/patient/${patientId}/recommendations`, {
      state: edit ? { edit: true } : null,
    });
  };

  const loadRecommendations = async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await GetData({
        module: "patient",
        block: "recommendations",
        id_patient: patientId,
        id_plan: currentPlanId,
        id_recommendation: currentRecommendationId,
      });

      if (isListMode) {
        const list = Array.isArray(data?.result)
          ? data.result
          : data?.result?.title?.value || data?.result?.description?.value
            ? [data.result]
            : [];
        setRecommendationsList(list);
        setRecommendations(emptyRecommendation());
        setSavedRecommendations(emptyRecommendation());
        setIsEditing(false);
      } else {
        const item = data?.result && !Array.isArray(data.result)
          ? data.result
          : emptyRecommendation();
        setRecommendations(item);
        setSavedRecommendations(item);
        setRecommendationsList([]);

        const shouldEdit = Boolean(location.state?.edit) && Boolean(item?.id?.value);
        setIsEditing(shouldEdit);
        if (shouldEdit) {
          navigate(location.pathname, { replace: true, state: null });
        }

        // Если запись не найдена — вернуться к списку
        if (!item?.id?.value && currentPlanId > 0) {
          goToList();
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const requestRecommendations = async ({
    action = "",
    id_template = "",
    id_recommendation = currentRecommendationId,
    payload = {},
  } = {}) => {
    return GetData({
      module: "patient",
      block: "recommendations",
      id_patient: patientId,
      id_plan: currentPlanId,
      id_recommendation: id_recommendation || 0,
      action,
      id_template,
      ...payload,
    });
  };

  const handleCloseTemplatesModal = () => {
    setShowTemplatesModal(false);
    setSelectedTemplate("");
    setTemplates([]);
  };

  const loadTemplates = async (source = templateType) => {
    setTemplatesLoading(true);
    setSelectedTemplate("");
    try {
      const response = await requestRecommendations({
        action: "get_recommendation_templates",
        id_recommendation: currentRecommendationId,
        payload: {
          template_source: source === "my" ? "my" : "common",
        },
      });
      setTemplates(response.templates || []);
    } catch (err) {
      setTemplates([]);
      showAlert(t("recommendations.alert.loadTemplatesFailed", { message: err.message }));
    } finally {
      setTemplatesLoading(false);
    }
  };

  const handleAddFromTemplate = async () => {
    setTemplateType("common");
    setShowTemplatesModal(true);
    await loadTemplates("common");
  };

  const handleTemplateTypeChange = async (nextType) => {
    if (nextType === templateType || templatesLoading) return;
    setTemplateType(nextType);
    await loadTemplates(nextType);
  };

  const handleSaveTemplate = async (template) => {
    const templateId = template?.id?.value || selectedTemplate;
    if (!templateId) {
      showAlert(t("recommendations.alert.selectTemplate"));
      return;
    }

    try {
      setTemplatesLoading(true);

      // В списке — создаём новую; в карточке — меняем текущую
      const targetRecommendationId = isListMode ? 0 : currentRecommendationId;

      const data = await requestRecommendations({
        action: "add_template",
        id_template: templateId,
        id_recommendation: targetRecommendationId,
      });

      if (data?.status?.message) {
        showAlert(data.status.message);
      }

      const selectedTemplateData = templates.find(
        (item) => String(item?.id?.value) === String(templateId),
      );
      await logPatientChange({
        idPatient: patientId,
        idItem: templateId,
        text: buildPatientLogText("patient_recommendations_template_applied", {
          details: `Применен шаблон: ${getTemplateDisplayName(selectedTemplateData, t)} (ID: ${normalizeDisplayValue(templateId)})`,
        }),
      });

      handleCloseTemplatesModal();
      await loadRecommendations();
    } catch (err) {
      showAlert(t("recommendations.alert.applyFailed", { message: err.message }));
    } finally {
      setTemplatesLoading(false);
    }
  };

  const handleCancel = () => {
    setRecommendations(savedRecommendations);
    setIsEditing(false);
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleBackToPlan = () => {
    if (currentPlanId > 0) {
      navigate(`/patient/${patientId}/plan/${currentPlanId}`);
      return;
    }
    navigate(`/patient/${patientId}/plan`);
  };

  const handleSave = async () => {
    if (
      !recommendations.title.value?.trim() ||
      !recommendations.description.value?.trim()
    ) {
      showAlert(t("recommendations.alert.fillAll"));
      return;
    }

    try {
      const data = await requestRecommendations({
        action: "change",
        id_recommendation: currentRecommendationId,
        payload: {
          title: recommendations.title.value,
          description: recommendations.description.value,
        },
      });

      if (data?.status?.message) {
        showAlert(data.status.message);
      }

      const previousTitle = savedRecommendations?.title?.value;
      const previousDescription = savedRecommendations?.description?.value;
      const nextTitle = recommendations?.title?.value;
      const nextDescription = recommendations?.description?.value;

      const changes = [];
      if (
        normalizeComparableValue(previousTitle) !==
        normalizeComparableValue(nextTitle)
      ) {
        changes.push({
          label: "Заголовок",
          from: previousTitle,
          to: nextTitle,
        });
      }
      if (
        normalizeComparableValue(previousDescription) !==
        normalizeComparableValue(nextDescription)
      ) {
        changes.push({
          label: "Описание",
          from: `${normalizeComparableValue(previousDescription).length} символов`,
          to: `${normalizeComparableValue(nextDescription).length} символов`,
        });
      }

      await logPatientChange({
        idPatient: patientId,
        text: buildPatientLogText("patient_recommendations_updated", {
          changes,
          details:
            changes.length === 0
              ? "Изменений в рекомендациях не обнаружено"
              : "",
        }),
      });

      const nextItem = data?.result && !Array.isArray(data.result)
        ? data.result
        : recommendations;
      setRecommendations(nextItem);
      setSavedRecommendations(nextItem);
      setIsEditing(false);
    } catch (err) {
      showAlert(t("recommendations.alert.saveFailed", { message: err.message }));
    }
  };

  const handleDelete = async (id) => {
    const targetId = Number(id);
    if (!Number.isFinite(targetId) || targetId <= 0) return;
    if (!window.confirm(t("recommendations.alert.confirmDelete"))) return;

    try {
      const data = await requestRecommendations({
        action: "delete",
        id_recommendation: targetId,
      });

      if (data?.status?.message) {
        showAlert(data.status.message);
      }

      if (!isListMode) {
        goToList();
        return;
      }

      await loadRecommendations();
    } catch (err) {
      showAlert(t("recommendations.alert.deleteFailed", { message: err.message }));
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setRecommendations((prev) => ({
      ...prev,
      [name]: { ...prev[name], value },
    }));
  };

  const handleDescriptionChange = (value) => {
    setRecommendations((prev) => ({
      ...prev,
      description: { ...prev.description, value },
    }));
  };

  if (loading)
    return <div>{t("recommendations.loading")}</div>;
  if (error)
    return <div>{t("error", { ns: "common", message: error })}</div>;

  const apt = appointmentsPrint?.result;
  const backButton = {
    label: (
      <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
        <svg
          viewBox="0 0 24 24"
          aria-hidden="true"
          width="20"
          height="20"
          style={{ color: "currentColor" }}>
          <path
            d="M9 14L4 9l5-5"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M4 9h11a4 4 0 1 1 0 8H9"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        {isListMode ? t("recommendations.backToPlan") : t("recommendations.backToList")}
      </span>
    ),
    onClick: isListMode ? handleBackToPlan : goToList,
    type: "normal icon",
  };

  return (
    <>
      {isListMode ? (
        <div className="patient-recommendations-view">
          <div className="patient-name-title-sticky">
            <BlockTitle
              title={t("recommendations.title")}
              buttons={[
                {
                  label: t("recommendations.add"),
                  onClick: handleAddFromTemplate,
                  type: "light",
                },
                backButton,
              ]}
            />
          </div>

          {recommendationsList.length === 0 ? (
            <div className="d-flex gap-3 mt-4">
              <div className="patient-recommendations">
                <button
                  className="button-default light"
                  onClick={handleAddFromTemplate}>
                  {t("recommendations.add")}
                </button>
              </div>
              <div className="patient-recommendations">
                <button className="button-default" onClick={handleBackToPlan}>
                  {t("recommendations.backToPlan")}
                </button>
              </div>
            </div>
          ) : (
            <div className="patient-recommendations mt-4">
              {recommendationsList.map((item) => {
                const id = item?.id?.value;
                return (
                  <div key={id} className="recommendation-item mb-4">
                    <div className="recommendation-item-header">
                      <h5 className="recommendation-item-title mb-0">
                        {item?.title?.value || "—"}
                      </h5>
                      <div className="plan-list-actions">
                        <button
                          type="button"
                          className="plan-list-action-btn plan-list-action-btn--text"
                          onClick={() => goToRecommendation(id, { edit: true })}>
                          {t("recommendations.edit")}
                        </button>
                        <button
                          type="button"
                          className="plan-list-action-btn plan-list-action-btn--text"
                          onClick={() => handleDelete(id)}>
                          {t("recommendations.delete")}
                        </button>
                      </div>
                    </div>
                    <TiptapViewer
                      content={item?.description?.value}
                      emptyText={t("recommendations.emptyDescription")}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : (
        <>
          <div className="patient-recommendations-view">
            <div className="patient-name-title-sticky">
              <BlockTitle
                title={t("recommendations.title")}
                buttons={
                  isEditing
                    ? [
                        { label: t("recommendations.save"), onClick: handleSave, type: "normal" },
                        { label: t("recommendations.cancel"), onClick: handleCancel, type: "light" },
                        backButton,
                      ]
                    : [
                        {
                          label: t("recommendations.changeTemplate"),
                          onClick: handleAddFromTemplate,
                          type: "light",
                        },
                        {
                          label: t("recommendations.delete"),
                          onClick: () => handleDelete(currentRecommendationId),
                          type: "light",
                        },
                        {
                          label: (
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "6px",
                              }}>
                              <svg
                                viewBox="0 0 24 24"
                                aria-hidden="true"
                                width="20"
                                height="20"
                                style={{ fill: "currentColor" }}>
                                <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zm2.92 2.33H5v-.92l9.06-9.06.92.92L5.92 19.58zM20.71 7.04a1.003 1.003 0 0 0 0-1.42L18.37 3.29a1.003 1.003 0 0 0-1.42 0l-1.83 1.83 3.75 3.75 1.84-1.83z" />
                              </svg>
                            </span>
                          ),
                          onClick: handleEdit,
                          type: "light icon",
                        },
                        backButton,
                      ]
                }
              />
            </div>

            {isEditing ? (
              <Form className="patient-recommendations mt-4">
                <Row className="mb-3">
                  <Col md={12}>
                    <Form.Group controlId="recommendationTitle">
                      <Form.Label>{t("recommendations.form.titleLabel")}</Form.Label>
                      <Form.Control
                        type="text"
                        name="title"
                        value={recommendations.title?.value || ""}
                        onChange={handleChange}
                        placeholder={t("recommendations.form.titlePlaceholder")}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md={12}>
                    <Form.Group controlId="recommendationDescription">
                      <Form.Label>{t("recommendations.form.descriptionLabel")}</Form.Label>
                      <TiptapEditor
                        value={recommendations.description?.value || ""}
                        onChange={handleDescriptionChange}
                        placeholder={t("recommendations.form.descriptionPlaceholder")}
                        minHeight="200px"
                      />
                      <Form.Text className="text-muted">
                        {t("recommendations.form.hint")}
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
              </Form>
            ) : (
              <div className="patient-recommendations mt-4">
                <div className="recommendation-item mb-4">
                  <h5>{recommendations.title?.value || "—"}</h5>
                  <TiptapViewer
                    content={recommendations.description?.value}
                    emptyText={t("recommendations.emptyList")}
                  />
                </div>
              </div>
            )}
          </div>

          <div className="patient-recommendations-print">
            <div className="patient-info-print">
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "140px 1fr",
                  gap: 12,
                  alignItems: "center",
                  marginBottom: 12,
                }}>
                <img
                  src={LogoText}
                  alt={t("recommendations.print.logoAlt")}
                  style={{ width: 140, height: 56, objectFit: "contain" }}
                />
                <div
                  style={{
                    fontWeight: 700,
                    fontSize: 18,
                    textTransform: "uppercase",
                    textAlign: "center",
                  }}>
                  <div>{t("recommendations.print.heading")}</div>
                </div>
              </div>
              <div className="info-print-first info-print-first--two-cols">
                <div className="info-item-print">
                  <div>{t("recommendations.print.fioLabel")} {apt?.fio?.value ?? "—"}</div>
                  <div>
                    {t("recommendations.print.birthDateLabel")}{" "}
                    {formatDateShortHeader(apt?.birthDate?.value)}
                  </div>
                  <div>{t("recommendations.print.cardLabel")} {apt?.cart_patient?.value ?? "—"}</div>
                  <div>
                    {t("recommendations.print.treatmentStartLabel")}{" "}
                    {formatDateShortHeader(
                      getFirstPlanOrActualDateFromAdmissions(apt?.admission),
                    )}
                  </div>
                </div>
                <div className="info-item-print">
                  <div>{t("recommendations.print.doctorLabel")} {apt?.doc_name?.value ?? "—"}</div>
                  <div>{t("recommendations.print.signatureLabel")}</div>
                </div>
              </div>
            </div>

            <div className="recommendations-print-title">
              {recommendations.title?.value || "—"}
            </div>
            <div className="recommendations-print-body recommendation-item">
              <TiptapViewer
                content={recommendations.description?.value}
                emptyText={t("recommendations.emptyList")}
              />
            </div>
          </div>
        </>
      )}

      <Modal show={showModal} onHide={handleCloseModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t("recommendations.modal.notification")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{modalMessage}</Modal.Body>
        <Modal.Footer>
          <div onClick={handleCloseModal} className="button-default">
            {t("recommendations.modal.ok")}
          </div>
        </Modal.Footer>
      </Modal>

      <SelectModal
        show={showTemplatesModal}
        onHide={handleCloseTemplatesModal}
        title={t("recommendations.templates.selectTitle")}
        loading={templatesLoading}
        options={templates}
        value={selectedTemplate}
        onChange={setSelectedTemplate}
        onSelect={handleSaveTemplate}
        optionLabel={(item) =>
          item.title?.value ||
          t("recommendations.templateFallback", { id: item.id?.value ?? "—" })
        }
        optionValue={(item) => String(item.id?.value)}
        confirmButtonText={t("recommendations.templates.confirm")}
        emptyMessage={t("recommendations.templates.empty")}
        loadingMessage={t("recommendations.templates.loading")}
        topContent={
          <div className="template-type-options mb-3">
            <div className="mb-2">
              <label>
                <input
                  type="radio"
                  name="recommendationTemplateType"
                  value="common"
                  checked={templateType === "common"}
                  onChange={() => handleTemplateTypeChange("common")}
                  disabled={templatesLoading}
                />{" "}
                {t("recommendations.templates.common")}
              </label>
            </div>
            <div>
              <label>
                <input
                  type="radio"
                  name="recommendationTemplateType"
                  value="my"
                  checked={templateType === "my"}
                  onChange={() => handleTemplateTypeChange("my")}
                  disabled={templatesLoading}
                />{" "}
                {t("recommendations.templates.my")}
              </label>
            </div>
          </div>
        }
      />
    </>
  );
}

export default PatientRecommendations;
